import jsPDF from "jspdf";

interface PDFData {
  transaction: any;
  items: any[];
  payments: any[];
  client: any;
  seller: any;
  tenant: any;
}

export async function generateTicketPDF(data: PDFData) {
  const { transaction, items, payments, client, seller, tenant } = data;

  // Ticket térmico geralmente tem 80mm de largura
  const ticketWidth = 80; // mm
  const margin = 5;
  
  // Função auxiliar para formatar valor do campo do formulário (precisa estar antes do cálculo)
  const normalizeType = (t: string) => {
    const s = (t || "").toLowerCase();
    if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
    if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
    if (["radio"].includes(s)) return "radio";
    if (["checkbox"].includes(s)) return "checkbox";
    if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
    if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
    if (["data", "date"].includes(s)) return "date";
    if (["data e hora", "datetime", "datahora"].includes(s)) return "datetime";
    return s;
  };

  const formatBRDate = (v: any, withTime = false): string => {
    if (!v) return "";
    if (typeof v === "string") {
      const s = v.trim();
      if (s.length >= 10 && s.includes("-")) {
        const [y, m, dAndTime] = s.split("-");
        const d = dAndTime.slice(0, 2);
        const base = `${d}/${m}/${y}`;
        if (withTime && s.includes("T")) {
          const time = s.split("T")[1].slice(0, 5);
          return `${base} ${time}`;
        }
        return base;
      }
      const dt = new Date(s);
      if (!isNaN(dt.getTime())) {
        const dd = String(dt.getDate()).padStart(2, "0");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const yyyy = dt.getFullYear();
        const base = `${dd}/${mm}/${yyyy}`;
        if (withTime) {
          const hh = String(dt.getHours()).padStart(2, "0");
          const min = String(dt.getMinutes()).padStart(2, "0");
          return `${base} ${hh}:${min}`;
        }
        return base;
      }
      return s;
    }
    try {
      const dt = new Date(v);
      if (!isNaN(dt.getTime())) {
        const dd = String(dt.getDate()).padStart(2, "0");
        const mm = String(dt.getMonth() + 1).padStart(2, "0");
        const yyyy = dt.getFullYear();
        const base = `${dd}/${mm}/${yyyy}`;
        if (withTime) {
          const hh = String(dt.getHours()).padStart(2, "0");
          const min = String(dt.getMinutes()).padStart(2, "0");
          return `${base} ${hh}:${min}`;
        }
        return base;
      }
    } catch {}
    return String(v);
  };

  const formatFormFieldValue = (field: any, value: any): string => {
    if (!value && value !== 0) return "";
    if (typeof value === "string") {
      const s = value.trim();
      if ((s.startsWith("[") && s.endsWith("]")) || (s.startsWith("{") && s.endsWith("}"))) {
        try { value = JSON.parse(s); } catch {}
      }
    }
    switch (normalizeType(field.tipo)) {
      case "select":
      case "radio":
        const option = field.form_field_options?.find((opt: any) => opt.id === value || opt.valor === value);
        return option ? option.label : String(value);
      case "date":
        return formatBRDate(value, false);
      case "datetime":
        return formatBRDate(value, true);
      case "multiselect":
      case "checkbox":
        if (Array.isArray(value)) {
          return value
            .map((v) => {
              const opt = field.form_field_options?.find((opt: any) => opt.id === v || opt.valor === v);
              return opt ? opt.label : String(v);
            })
            .join(", ");
        }
        return String(value);
      case "file":
      case "image":
        if (value && typeof value === "object") {
          const name = (value as any).original_name || (value as any).path || "arquivo";
          return name;
        }
        return String(value);
      default:
        return String(value);
    }
  };
  
  // Calcular altura aproximada necessária para papel contínuo
  // Vamos calcular de forma mais conservadora para garantir que não corte
  let estimatedHeight = 0;
  
  // Header: nome do tenant (pode quebrar linha) + email + separador
  const tenantName = tenant?.name || "EMPRESA";
  const maxWidth = ticketWidth - margin * 2;
  // Estimar linhas do nome (assumindo fonte 14)
  const estimatedNameLines = Math.ceil(tenantName.length / 20); // ~20 chars por linha em 80mm
  estimatedHeight += estimatedNameLines * 5; // ~5mm por linha
  estimatedHeight += 1; // espaço
  if (tenant?.email) {
    estimatedHeight += 4; // email
    estimatedHeight += 1; // espaço
  }
  estimatedHeight += 3; // separador
  estimatedHeight += 1.5; // espaço após separador
  
  // COMPROVANTE DE VENDA
  estimatedHeight += 4; // título
  estimatedHeight += 1.5; // espaço
  
  // Informações da venda (7 linhas possíveis)
  estimatedHeight += 4; // Nº
  estimatedHeight += 4; // Data da Venda
  if (transaction?.data_entrega) {
    estimatedHeight += 4; // Data de Entrega
  }
  estimatedHeight += 4; // Cliente
  if (client?.telefone) {
    estimatedHeight += 4; // Tel
  }
  if (seller?.nome_completo) {
    estimatedHeight += 4; // Vendedor
  }
  if (client?.email) {
    estimatedHeight += 4; // Email
  }
  estimatedHeight += 3; // separador
  estimatedHeight += 3; // espaço
  
  // ITENS
  estimatedHeight += 4; // título
  estimatedHeight += 3; // espaço
  
  // Itens: calcular altura mais precisa
  items.forEach((item, index) => {
    if (index > 0) {
      estimatedHeight += 0.5; // espaço antes do traço
      estimatedHeight += 0.1; // traço
      estimatedHeight += 4; // espaço após traço
    }
    
    estimatedHeight += 3; // nome do produto
    estimatedHeight += 1; // espaço entre produto e formulário
    
    const hasForm = item.form_fields && item.form_fields.length > 0 && item.form_values;
    if (hasForm) {
      const formFieldsWithValues = item.form_fields.filter((field: any) => 
        item.form_values?.[field.slug] !== undefined && 
        item.form_values?.[field.slug] !== null && 
        item.form_values?.[field.slug] !== ""
      );
      // Estimar linhas do formulário (pode quebrar)
      let formTextLength = 0;
      formFieldsWithValues.forEach((field: any) => {
        const value = item.form_values[field.slug];
        const formattedValue = formatFormFieldValue(field, value);
        formTextLength += field.nome.length + formattedValue.length + 3; // +3 para ": " e " • "
      });
      const formLines = Math.max(1, Math.ceil(formTextLength / 30)); // ~30 chars por linha
      estimatedHeight += formLines * 3; // ~3mm por linha
      estimatedHeight += 5; // espaço após formulário
    }
    
    estimatedHeight += 4; // quantidade e preço
    if (index === items.length - 1) {
      estimatedHeight += 4; // espaço extra no último item
    }
  });
  
  estimatedHeight += 3; // separador
  estimatedHeight += 3; // espaço
  
  // Calcular valores dos totais e pagamentos (serão reutilizados na renderização)
  const subtotal = items.reduce((sum, item) => sum + item.total, 0);
  const desconto = transaction?.desconto || 0;
  const total = subtotal - desconto;
  const totalPago = payments
    .filter((p) => p.status === "confirmado")
    .reduce((sum, p) => sum + p.valor, 0);
  const saldo = total - totalPago;
  
  // Totais
  estimatedHeight += 4; // Subtotal
  estimatedHeight += 4; // TOTAL
  if (desconto > 0) {
    estimatedHeight += 4; // Desconto
  }
  estimatedHeight += 5; // espaço
  
  // Pagamentos
  if (payments.length > 0) {
    estimatedHeight += 3; // separador
    estimatedHeight += 3; // espaço
    estimatedHeight += 4; // título PAGAMENTOS
    estimatedHeight += 3; // espaço
    payments.forEach(() => {
      estimatedHeight += 3; // data e método
      estimatedHeight += 2; // espaço
      estimatedHeight += 3; // valor
      estimatedHeight += 2; // espaço
      estimatedHeight += 3; // status
      estimatedHeight += 2; // espaço
    });
    estimatedHeight += 4; // Total Pago
    if (saldo > 0) {
      estimatedHeight += 4; // Saldo
    }
  }
  
  estimatedHeight += 3; // separador
  estimatedHeight += 3; // espaço
  
  // Footer
  estimatedHeight += 3; // "Obrigado pela preferência!"
  estimatedHeight += 4; // espaço
  if (tenant?.name) {
    estimatedHeight += 3; // nome tenant
  }
  if (tenant?.email) {
    estimatedHeight += 3; // email tenant
  }
  estimatedHeight += 3; // espaço
  estimatedHeight += 3; // data/hora
  
  // Margem de segurança mínima (5% do total)
  estimatedHeight = estimatedHeight * 1.05;
  
  // Altura mínima para papel contínuo
  const minHeight = 50;
  const initialHeight = Math.max(minHeight, Math.ceil(estimatedHeight));
  
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: [ticketWidth, initialHeight],
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = margin;

  // Helper para adicionar texto centralizado
  const addCenteredText = (text: string, y: number, fontSize: number = 10, bold: boolean = false) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    const textWidth = doc.getTextWidth(text);
    doc.text(text, (pageWidth - textWidth) / 2, y);
    return fontSize * 0.35; // altura aproximada
  };

  // Helper para adicionar texto
  const addText = (text: string, x: number, y: number, fontSize: number = 8, bold: boolean = false) => {
    doc.setFontSize(fontSize);
    doc.setFont("helvetica", bold ? "bold" : "normal");
    const maxWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return lines.length * fontSize * 0.35;
  };

  // Linha separadora
  const addSeparator = (y: number) => {
    doc.setLineWidth(0.3);
    doc.line(margin, y, pageWidth - margin, y);
    return 3;
  };

  // Header - permitir quebra de linha se não couber
  // tenantName já foi declarado acima no cálculo de altura
  // Recalcular maxWidth usando pageWidth (valor real do documento)
  const headerMaxWidth = pageWidth - margin * 2;
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  const tenantNameLines = doc.splitTextToSize(tenantName, headerMaxWidth);
  // Centralizar cada linha individualmente
  tenantNameLines.forEach((line: string) => {
    const lineWidth = doc.getTextWidth(line);
    const lineX = (pageWidth - lineWidth) / 2;
    doc.text(line, lineX, yPos);
    yPos += 4;
  });
  yPos += 1;

  if (tenant?.email) {
    yPos += addCenteredText(tenant.email, yPos, 8, false);
    yPos += 1;
  }

  yPos += addSeparator(yPos);
  yPos += 1.5;

  // Informações da Venda
  yPos += addCenteredText("COMPROVANTE DE VENDA", yPos, 10, true);
  yPos += 1.5;

  // Reorganizar: Nº na primeira linha, resto uma informação por linha
  doc.setFontSize(8);
  
  // Linha 1: Nº
  const numeroLabel = "Nº:";
  const numeroValor = transaction?.id?.substring(0, 8) || "N/A";
  doc.setFont("helvetica", "bold");
  doc.text(numeroLabel, margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${numeroValor}`, margin + doc.getTextWidth(numeroLabel), yPos);
  yPos += 4;

  // Linha 2: Data da Venda (uma informação por linha)
  const dataVendaLabel = "Data da Venda:";
  const dataVendaValor = transaction?.data
    ? new Date(transaction.data).toLocaleDateString("pt-BR")
    : new Date().toLocaleDateString("pt-BR");
  doc.setFont("helvetica", "bold");
  doc.text(dataVendaLabel, margin, yPos);
  doc.setFont("helvetica", "normal");
  doc.text(`   ${dataVendaValor}`, margin + doc.getTextWidth(dataVendaLabel), yPos);
  yPos += 4;

  // Linha 3: Data de Entrega (se houver)
  const dataEntregaLabel = "Data de Entrega:";
  const dataEntregaValor = transaction?.data_entrega
    ? new Date(transaction.data_entrega).toLocaleDateString("pt-BR")
    : null;
  
  if (dataEntregaValor) {
    doc.setFont("helvetica", "bold");
    doc.text(dataEntregaLabel, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${dataEntregaValor}`, margin + doc.getTextWidth(dataEntregaLabel), yPos);
    yPos += 4;
  }

  // Linha 4: Cliente
  if (client?.nome) {
    const clienteLabel = "Cliente:";
    const clienteTexto = client.nome;
    doc.setFont("helvetica", "bold");
    doc.text(clienteLabel, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${clienteTexto}`, margin + doc.getTextWidth(clienteLabel), yPos);
    yPos += 4;
  }

  // Linha 5: Tel: (em linha própria após o cliente)
  if (client?.telefone) {
    const telefoneLabel = "Tel:";
    const telefoneTexto = client.telefone;
    doc.setFont("helvetica", "bold");
    doc.text(telefoneLabel, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${telefoneTexto}`, margin + doc.getTextWidth(telefoneLabel), yPos);
    yPos += 4;
  }

  // Linha 6: Vendedor
  if (seller?.nome_completo) {
    const vendedorLabel = "Vendedor:";
    const vendedorValor = seller.nome_completo;
    doc.setFont("helvetica", "bold");
    doc.text(vendedorLabel, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${vendedorValor}`, margin + doc.getTextWidth(vendedorLabel), yPos);
    yPos += 4;
  }

  // Linha 7: Email (se houver)
  if (client?.email) {
    const emailLabel = "Email:";
    const emailTexto = client.email;
    doc.setFont("helvetica", "bold");
    doc.text(emailLabel, margin, yPos);
    doc.setFont("helvetica", "normal");
    doc.text(`   ${emailTexto}`, margin + doc.getTextWidth(emailLabel), yPos);
    yPos += 4;
  }

  yPos += addSeparator(yPos);
  yPos += 3;

  // Itens
  yPos += addCenteredText("ITENS", yPos, 10, true);
  yPos += 3;

  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    // Adicionar linha separadora mais fina ANTES do produto (exceto o primeiro)
    if (index > 0) {
      // Espaçamento antes do traço
      yPos += 0.5;
      // Desenhar o traço
      doc.setLineWidth(0.1);
      doc.setDrawColor(200, 200, 200); // Cinza claro
      doc.line(margin, yPos, pageWidth - margin, yPos);
      // Espaçamento abaixo do traço (margin-bottom aumentado) antes do produto
      yPos += 4;
    }

    yPos += addText(`${index + 1}. ${item.product?.nome || "Produto"}`, margin, yPos, 8, true);
    yPos += 1; // Reduzir espaço entre produto e formulário

    // Imagens vinculadas aos campos (image ou file com mime image/*)
    if (item.form_fields && item.form_fields.length > 0 && item.form_values) {
      const imageEntries = item.form_fields
        .map((field: any) => ({ field, value: item.form_values?.[field.slug] }))
        .filter(({ field, value }) => {
          if (!value) return false;
          const isFieldImage = field.tipo === "image";
          const isFileImage = field.tipo === "file" && typeof value === "object" && ((value?.mime_type || "").startsWith("image/"));
          return isFieldImage || isFileImage;
        }) as Array<{ field: any; value: any }>;

      const loadImageDataUrl = async (url: string) => {
        try {
          const res = await fetch(url);
          const blob = await res.blob();
          const reader = new FileReader();
          const dataUrl: string = await new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
          const img = new Image();
          await new Promise((resolve) => {
            img.onload = resolve;
            img.src = dataUrl;
          });
          return { dataUrl, widthPx: img.naturalWidth || 300, heightPx: img.naturalHeight || 300 };
        } catch {
          return null;
        }
      };

      if (imageEntries.length > 0) {
        const maxImageWidthMm = pageWidth - margin * 2 - 4;
        for (const entry of imageEntries) {
          const url = (entry.value?.signed_url as string) || "";
          if (!url) continue;
          const imgData = await loadImageDataUrl(url);
          if (!imgData) continue;
          const ratio = imgData.heightPx / imgData.widthPx;
          const imgWidthMm = Math.min(maxImageWidthMm, 40);
          const imgHeightMm = imgWidthMm * ratio;
          doc.addImage(imgData.dataUrl, undefined as any, margin + 2, yPos, imgWidthMm, imgHeightMm);
          yPos += imgHeightMm + 2;
        }
      }
    }

    // Valores dos campos do formulário
    if (item.form_fields && item.form_fields.length > 0 && item.form_values) {
      const formFieldsWithValues = item.form_fields.filter((field: any) => 
        item.form_values?.[field.slug] !== undefined && 
        item.form_values?.[field.slug] !== null && 
        item.form_values?.[field.slug] !== ""
      );
      
      if (formFieldsWithValues.length > 0) {
        // Construir texto com labels em negrito
        doc.setFontSize(6);
        const maxWidth = pageWidth - margin * 2;
        let currentX = margin + 3;
        let currentY = yPos;
        
        formFieldsWithValues.forEach((field: any, fieldIndex: number) => {
          const value = item.form_values[field.slug];
          const formattedValue = formatFormFieldValue(field, value);
          
          // Label em negrito
          doc.setFont("helvetica", "bold");
          const labelText = `${field.nome}: `;
          const labelWidth = doc.getTextWidth(labelText);
          
          // Verificar se cabe na linha atual
          if (currentX + labelWidth + doc.getTextWidth(formattedValue) > pageWidth - margin - 3) {
            currentY += 3;
            currentX = margin + 3;
          }
          
          doc.text(labelText, currentX, currentY);
          currentX += labelWidth;
          
          // Valor em normal
          doc.setFont("helvetica", "normal");
          const valueText = formattedValue;
          const valueWidth = doc.getTextWidth(valueText);
          
          // Verificar se o valor cabe
          if (currentX + valueWidth > pageWidth - margin - 3) {
            currentY += 3;
            currentX = margin + 3;
            doc.text(valueText, currentX, currentY);
            currentX += doc.getTextWidth(valueText);
          } else {
            doc.text(valueText, currentX, currentY);
            currentX += valueWidth;
          }
          
          // Separador
          if (fieldIndex < formFieldsWithValues.length - 1) {
            doc.setFont("helvetica", "normal");
            const separator = " • ";
            const separatorWidth = doc.getTextWidth(separator);
            
            if (currentX + separatorWidth > pageWidth - margin - 3) {
              currentY += 3;
              currentX = margin + 3;
            } else {
              doc.text(separator, currentX, currentY);
              currentX += separatorWidth;
            }
          }
        });
        
        yPos = currentY + 5; // Aumentar espaço após formulário
      }
    }

    // Alinhar quantidade e valores à direita
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    const qtdPrecoText = `${item.quantidade}x ${new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(item.preco_unitario)} = ${new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(item.total)}`;
    const textWidth = doc.getTextWidth(qtdPrecoText);
    doc.text(qtdPrecoText, pageWidth - margin - textWidth, yPos);
    yPos += 4;
    
    if (index === items.length - 1) {
      yPos += 4;
    }
  }

  yPos += addSeparator(yPos);
  yPos += 3;

  // Totais
  // subtotal, desconto, total, totalPago e saldo já foram calculados acima no cálculo de altura
  // Reutilizar os valores calculados

  yPos += addText("Subtotal:", margin, yPos, 8);
  doc.text(
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(subtotal),
    pageWidth - margin,
    yPos,
    { align: "right" }
  );
  yPos += 4;

  if (desconto > 0) {
    yPos += addText("Desconto:", margin, yPos, 8);
    doc.text(
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(desconto),
      pageWidth - margin,
      yPos,
      { align: "right" }
    );
    yPos += 4;
  }

  yPos += addText("TOTAL:", margin, yPos, 10, true);
  doc.setFont("helvetica", "bold");
  doc.text(
    new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(total),
    pageWidth - margin,
    yPos,
    { align: "right" }
  );
  yPos += 5;

  // Pagamentos
  if (payments.length > 0) {
    yPos += addSeparator(yPos);
    yPos += 3;

    yPos += addCenteredText("PAGAMENTOS", yPos, 10, true);
    yPos += 3;

    payments.forEach((payment) => {
      // Usar payment.data se disponível, senão usar created_at ou confirmed_at
      const paymentDate = payment.data || payment.confirmed_at || payment.created_at;
      const dateText = paymentDate 
        ? new Date(paymentDate).toLocaleDateString("pt-BR")
        : "N/A";
      yPos += addText(
        `${dateText} - ${payment.payment_method?.nome || "N/A"}`,
        margin,
        yPos,
        7
      );
      yPos += 2;

      const paymentValorText = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(payment.valor);
      const paymentValorTextWidth = doc.getTextWidth(paymentValorText);
      doc.text(paymentValorText, pageWidth - margin - paymentValorTextWidth, yPos - 2);

      if (payment.status === "confirmado") {
        doc.setTextColor(0, 128, 0);
      } else {
        doc.setTextColor(255, 165, 0);
      }
      yPos += addText(
        `  Status: ${payment.status === "confirmado" ? "Confirmado" : "Pendente"}`,
        margin,
        yPos,
        7
      );
      doc.setTextColor(0, 0, 0);
      yPos += 3;
    });

    yPos += 2;
    yPos += addText("Total Pago:", margin, yPos, 8);
    const totalPagoText = new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(totalPago);
    const totalPagoTextWidth = doc.getTextWidth(totalPagoText);
    doc.text(totalPagoText, pageWidth - margin - totalPagoTextWidth, yPos);
    yPos += 4;

    if (saldo > 0) {
      doc.setTextColor(255, 0, 0);
      yPos += addText("SALDO:", margin, yPos, 9, true);
      doc.setFont("helvetica", "bold");
      const saldoText = new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(saldo);
      const saldoTextWidth = doc.getTextWidth(saldoText);
      doc.text(saldoText, pageWidth - margin - saldoTextWidth, yPos);
      doc.setTextColor(0, 0, 0);
      yPos += 4;
    }
  }

  yPos += addSeparator(yPos);
  yPos += 3;

  // Footer
  yPos += addCenteredText("Obrigado pela preferência!", yPos, 8);
  yPos += 4;

  if (tenant?.name) {
    yPos += addCenteredText(tenant.name, yPos, 7);
  }

  if (tenant?.email) {
    yPos += addCenteredText(tenant.email, yPos, 7);
  }

  yPos += 3;
  yPos += addCenteredText(
    new Date().toLocaleString("pt-BR"),
    yPos,
    7
  );

  // Para papel contínuo, a altura já foi calculada dinamicamente
  // Não há necessidade de ajustar, pois é uma única página com altura variável
  
  // Abrir PDF no navegador
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, "_blank");
  // Limpar o URL após um tempo para liberar memória
  setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
}

